import React, { useRef, useState, useEffect, useMemo } from "react";
import Button from "../../Button";
import classnames from "classnames";
import {
    frequencies,
    getPlanData,
    getPlanPricing,
    getCorePlanTiers,
    getDefaultCoreTierIndex
} from "../../../../../clientServices/workspaceFeaturesCredits";
import { getPricingTablePlans } from "@functions/plans/utilities";
import { Plan } from "@functions/plans/types";
import { CreditTierSelector } from "../../UpgradeModal/Credits/CreditTierSelector";
import { PlanButton, renderPlanColumn } from "../../UpgradeModal/Credits/PlansComparisonCredits";
import RowCard from "../../RowCard";
import { WorkspaceUsage } from "../../../../../state/types";
import { useProjectSelector } from "../../../hooks/useProjectSelector";
import { trialDurationLabel } from "@functions/plans/trialConstants";
import { TrialStartBanner } from "../../Trial/TrialStartBanner";

export function ChangeWorkspacePlanTableCredits({
    usage,
    onDowngradeClick,
    onBuyClick,
    onStartTrial,
    onExtendTrial,
    showTrialExpired,
    showBlockedSandboxModal,
    isEligibleForTrial,
    isStartingTrial,
    hasExtendedTrial,
    scrollPosition
}: {
    usage: WorkspaceUsage;
    onDowngradeClick: (plan, frequency) => void;
    onBuyClick: (
        planType: string,
        priceId: string,
        totalPrice: string,
        frequency: string,
        tierCredits?: number
    ) => void;
    onStartTrial: () => Promise<void>;
    onExtendTrial: () => void;
    showTrialExpired: boolean;
    showBlockedSandboxModal: boolean;
    isEligibleForTrial: boolean;
    isStartingTrial: boolean;
    hasExtendedTrial: boolean;
    scrollPosition: number;
}) {
    const [frequency, setFrequency] = useState(frequencies[1]);
    const [isProcessing, setIsProcessing] = useState(null);
    const projectSelector = useProjectSelector();
    const planPricing = useMemo(() => getPlanPricing(projectSelector), [projectSelector]);

    const corePlanTiers = useMemo(() => getCorePlanTiers(projectSelector), [projectSelector]);
    const [selectedCoreTierIndex, setSelectedCoreTierIndex] = useState(() =>
        getDefaultCoreTierIndex(corePlanTiers)
    );
    const selectedCoreTier = corePlanTiers[selectedCoreTierIndex] || corePlanTiers[0];
    const isAnnual = frequency.interval === "annually";

    const canExtendTrial = showTrialExpired && !hasExtendedTrial;
    const isBlockingModal = showTrialExpired || showBlockedSandboxModal;

    const handleBuyClick = async ({ planType }) => {
        setIsProcessing(planType);
        if (planType === "core") {
            const tierPrice = isAnnual
                ? selectedCoreTier.annualPrice
                : selectedCoreTier.monthlyPrice;
            await onBuyClick(
                planType,
                planPricing[planType][frequency.interval].priceId,
                `$${isAnnual ? tierPrice * 12 : tierPrice}`,
                frequency.interval,
                selectedCoreTier.credits
            );
        } else {
            await onBuyClick(
                planType,
                planPricing[planType][frequency.interval].priceId,
                planPricing[planType][frequency.interval].totalPrice,
                frequency.interval
            );
        }
        setIsProcessing(null);
    };

    const handleDowngradeClick = (plan) => {
        onDowngradeClick(plan, frequency);
    };

    // Use plan visibility rules from @functions/plans so delisted plans only appear when the workspace is actually on them.
    const trialPlanKey = "trial";
    const plansOnTable = getPricingTablePlans(trialPlanKey as any);
    const planData = getPlanData(
        trialPlanKey,
        frequency,
        plansOnTable as any,
        usage,
        projectSelector,
        { selectedCoreTier }
    );

    const handleSalesFormClick = () => {
        window?.open("https://roboflow.com/sales", "_blank");
        window?.eventTracker?.event({
            type: "Clicked Sales form",
            data: {
                clickedFrom: "change_workspace_plan_credits",
                workspaceId: window?.currentWorkspace,
                uid: window?.user?.uid,
                currentPlan: window?.usage?.plan
            }
        });
    };

    const bottomDivRef = useRef(null);
    const scrollToBottom = () => {
        bottomDivRef.current.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div className="trialDialog flex h-full w-full items-start justify-center">
            <div
                className={classnames(
                    "flex w-full max-w-5xl items-center justify-center",
                    isBlockingModal ? "py-10" : ""
                )}
            >
                <div
                    className={classnames(
                        "relative flex w-full flex-col justify-center gap-4 rounded-lg bg-white",
                        isBlockingModal ? "p-8" : "p-0"
                    )}
                >
                    {/* Section Header */}
                    <div
                        className={classnames(
                            "flex w-full flex-col items-center justify-between gap-3 md:flex-row",
                            isBlockingModal ? "" : "md:pr-8"
                        )}
                    >
                        <div className="flex flex-col items-center gap-2 md:items-start">
                            <div className="text-xl font-semibold text-gray-900">
                                {showTrialExpired
                                    ? "Your Roboflow Trial has Expired"
                                    : showBlockedSandboxModal
                                      ? "Your Subscription has Ended"
                                      : "Change Plan"}
                            </div>

                            {isBlockingModal && (
                                <div className="text-sm text-secondary">
                                    Please choose a plan to continue using the platform.
                                </div>
                            )}
                        </div>
                        <Button type="secondary" onClick={handleSalesFormClick}>
                            Interested in custom plans?
                            <i className="far fa-arrow-up-right"></i>
                        </Button>
                    </div>

                    {scrollPosition < 200 && (
                        <Button
                            type="upgrade"
                            size="medium"
                            onClick={scrollToBottom}
                            className="fixed left-[50%] z-50 hidden md:bottom-[5%] md:flex"
                        >
                            More Options
                            <i className="far fa-arrow-down"></i>
                        </Button>
                    )}

                    {showBlockedSandboxModal && isEligibleForTrial && (
                        <TrialStartBanner
                            title={`Try Roboflow Premium free for ${trialDurationLabel}`}
                            description="Keep your workflow moving while you evaluate premium features."
                            onStartTrial={onStartTrial}
                            isLoading={isStartingTrial}
                            className="mt-2"
                        />
                    )}

                    <div className="flex flex-col items-center gap-3 md:gap-0">
                        <div className="relative mt-2 flex flex-col-reverse gap-1 divide-y divide-y-reverse divide-brand overflow-visible rounded-md border border-purboflow-300 bg-white transition duration-200 md:flex-row md:divide-x md:divide-y-0 lg:mt-4">
                            {Object.entries(planData).map(([planKey, planDetails]) =>
                                renderPlanColumn({
                                    planKey,
                                    planDetails,
                                    getPlanButton: () => (
                                        <PlanButton
                                            planKey={planKey}
                                            planDetails={planDetails}
                                            usage={usage}
                                            handlePlanDowngrade={() =>
                                                handleDowngradeClick(planKey)
                                            }
                                            handleSalesFormClick={handleSalesFormClick}
                                            currentPlan={window?.usage?.plan}
                                            frequency={frequency}
                                            isProcessing={isProcessing}
                                            handlePlanPurchase={handleBuyClick}
                                        />
                                    ),
                                    frequency,
                                    setFrequency,
                                    alwaysShowPublic: true,
                                    tierSelectorSlot:
                                        planKey === Plan.UbpCore ? (
                                            <CreditTierSelector
                                                tiers={corePlanTiers}
                                                selectedTier={selectedCoreTier}
                                                onSelect={(tier) => {
                                                    const idx = corePlanTiers.indexOf(tier);
                                                    setSelectedCoreTierIndex(idx >= 0 ? idx : 0);
                                                }}
                                                isAnnual={isAnnual}
                                                baseTier={corePlanTiers[0]}
                                                compact={plansOnTable.length > 3}
                                            />
                                        ) : null,
                                    priceOverride:
                                        planKey === Plan.UbpCore
                                            ? {
                                                  price: isAnnual
                                                      ? selectedCoreTier.annualPrice
                                                      : selectedCoreTier.monthlyPrice,
                                                  monthlyPrice: selectedCoreTier.monthlyPrice
                                              }
                                            : null
                                })
                            )}
                        </div>
                    </div>
                    <RowCard
                        icon="far fa-circle-info"
                        description="Looking for security review, a custom plan, or a bespoke solution?"
                        purple={true}
                        button={
                            <Button type="primary" onClick={handleSalesFormClick}>
                                Talk to Sales
                            </Button>
                        }
                    />

                    <div className="settingsContainer !mt-4 !p-0">
                        <div className="settingsCard flex flex-col justify-between !gap-3">
                            {isBlockingModal && (
                                <div className="flex w-full flex-row items-center justify-between gap-2">
                                    <div className="flex flex-row gap-3">
                                        <div className="text-xl font-semibold text-gray-900">
                                            Not Ready to Purchase?
                                        </div>
                                    </div>
                                </div>
                            )}

                            {canExtendTrial && <ExtendTrialCallout onExtendTrial={onExtendTrial} />}
                            <EmailCallout isStarterTrial={showTrialExpired} />
                            <div ref={bottomDivRef} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const PlanPrice = ({
    planType,
    frequency,
    priceOverride
}: {
    planType: "basic" | "core" | "growth-ubp";
    frequency: any;
    priceOverride?: { price: number; monthlyPrice?: number } | null;
}) => {
    const projectSelector = useProjectSelector();
    const planPricing = useMemo(() => getPlanPricing(projectSelector), [projectSelector]);
    const isNumericPrice = ["basic", "core", "growth-ubp"].includes(planType);
    const displayPrice = priceOverride
        ? priceOverride.price
        : planPricing[planType][frequency.interval].price;
    const showStrikethrough = priceOverride
        ? priceOverride.monthlyPrice && frequency.discountRate
        : frequency.discountRate;
    const strikethroughPrice = priceOverride
        ? priceOverride.monthlyPrice
        : planPricing[planType]["monthly"].price;
    return (
        <div className="flex flex-col items-start gap-1.5">
            <div className="flex flex-col">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0">
                    <div className="text-2xl font-semibold text-gray-900">
                        {isNumericPrice ? `$${displayPrice}` : displayPrice}
                    </div>
                    {isNumericPrice && showStrikethrough && (
                        <div className="text-lg font-normal text-tertiary line-through">
                            ${strikethroughPrice}
                        </div>
                    )}
                </div>
                <span className="text-sm font-normal text-gray-500">
                    {planPricing[planType][frequency.interval].priceSuffix}
                </span>
            </div>
        </div>
    );
};

export const PlanPrivacy = ({ planKey, planDetails }) => {
    return (
        <div className="flex flex-row items-center gap-2 self-stretch border-b-1 border-t-1 border-gray-200 py-4 text-sm text-primary">
            <i className={planKey === "public" ? planDetails.icon : "far fa-lock"}></i>
            <p className={planKey === "enterprise" ? "text-sm font-normal text-primary" : ""}>
                Data and Models are{" "}
                <span
                    className={`${planKey === "enterprise" ? "font-bold" : "text-sm font-bold"} text-brand`}
                >
                    {planKey === "public" ? "public" : "private"}
                </span>
                {"."}
            </p>
        </div>
    );
};

export const PlanFrequencySelector = ({ frequencyOptions, frequency, setFrequency }) => (
    <div className="flex w-full flex-col items-center">
        <div className="flex w-full rounded-full border border-gray-200 bg-white p-0.5">
            {frequencyOptions.map((option) => {
                const isSelected = frequency.interval === option.interval;
                const isAnnual = option.interval === "annually";
                return (
                    <button
                        key={option.interval}
                        type="button"
                        onClick={() => setFrequency(option)}
                        className={classnames(
                            "w-full rounded-full px-3 py-1.5 text-xs font-medium transition duration-200",
                            isSelected
                                ? "bg-yellow-100 text-yellow-800"
                                : "text-gray-500 hover:text-gray-700"
                        )}
                    >
                        {option.labelWithoutDiscount}
                        {isAnnual && frequency.interval !== "annually" && (
                            <span> (Save up to 20%)</span>
                        )}
                    </button>
                );
            })}
        </div>
    </div>
);

const EmailCallout = ({ isStarterTrial }) => {
    const handleEmailClick = () => {
        let subject = isStarterTrial ? "Roboflow%20Trial" : "Roboflow%20Subscription";
        window.open(`mailto:growth@roboflow.com?subject=${subject}`, "_blank");
        eventTracker.event({
            type: "Clicked Growth Email",
            data: {
                clickedFrom: "starter_plan_trial_modal",
                workspaceId: window.currentWorkspace,
                uid: window.user.uid,
                currentPlan: window.usage?.plan
            }
        });
    };

    return (
        <RowCard
            icon="far fa-envelope"
            title="Have questions?"
            description="Get in touch with a team member to learn more about Roboflow and our customized plans."
            button={
                <Button type="secondary" onClick={handleEmailClick}>
                    Email Us
                    <i className="far fa-arrow-right"></i>
                </Button>
            }
        />
    );
};

const ExtendTrialCallout = ({ onExtendTrial }) => {
    const [isExtendTrialProcessing, setIsExtendTrialProcessing] = useState(false);

    const handleExtendTrial = async () => {
        setIsExtendTrialProcessing(true);
        await onExtendTrial();
        setIsExtendTrialProcessing(false);
    };

    return (
        <RowCard
            icon="far fa-calendar"
            title="Extend trial by 7 days"
            description="Continue enjoying key features and benefits with a 7 day trial extension - on the house."
            button={
                <Button
                    type="secondary"
                    onClick={handleExtendTrial}
                    loading={isExtendTrialProcessing}
                >
                    Extend trial
                    <i className="far fa-arrow-right"></i>
                </Button>
            }
        />
    );
};
